/*
 * Al-Qalam display currency converter (no app).
 * - Detects the visitor's country with Shopify's own browsing-context endpoint and picks a matching currency once.
 * - The visitor can change it from the top bar picker; the choice is remembered on this device.
 * - Converts every price shown inside <bdi> tags (the theme wraps all prices in <bdi>) using Shopify's daily CDN rates.
 * - Display only: cart, checkout and orders stay in INR, and the cart shows a note saying so.
 */
(() => {
  const STORE = 'INR';
  const CHOICE_KEY = 'qc-currency';
  const AUTO_KEY = 'qc-currency-auto';
  const AMOUNT = /₹\s?([\d,]+(?:\.\d+)?)/g;

  const COUNTRY_CURRENCY = {
    IN: 'INR', US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
    AE: 'AED', SA: 'SAR', KW: 'KWD', QA: 'QAR', BH: 'BHD', OM: 'OMR',
    SG: 'SGD', MY: 'MYR', JP: 'JPY', CH: 'CHF', BD: 'BDT', NP: 'NPR', LK: 'LKR', ZA: 'ZAR',
  };
  const EURO = ['AT', 'BE', 'BG', 'HR', 'CY', 'DE', 'EE', 'FI', 'FR', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'];
  EURO.forEach((code) => { COUNTRY_CURRENCY[code] = 'EUR'; });

  const rates = () => (window.Currency && window.Currency.rates) || null;
  const supported = (code) => {
    const r = rates();
    return !!code && (code === STORE || (r && r[code] && document.querySelector(`[data-qc-option="${code}"]`)));
  };

  // Show the chosen currency's flag and code on every trigger, and mark it in the lists.
  const syncPickers = () => {
    document.querySelectorAll('[data-qc-option]').forEach((option) => {
      const active = option.dataset.qcOption === current;
      option.setAttribute('aria-current', active ? 'true' : 'false');
      if (!active) return;
      const wrap = option.closest('[data-qc-wrap]');
      const flag = wrap?.querySelector('[data-qc-current-flag]');
      const code = wrap?.querySelector('[data-qc-current-code]');
      if (flag) flag.innerHTML = option.querySelector('.qc-flag')?.innerHTML || '';
      if (code) code.textContent = current;
    });
  };

  let current = STORE;
  const formatters = {};
  const format = (amount, code) => {
    formatters[code] ||= new Intl.NumberFormat('en', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' });
    return formatters[code].format(amount);
  };
  const convert = (inr) => {
    const r = rates();
    return (inr * r[STORE]) / r[current];
  };

  const convertElement = (el) => {
    const text = el.textContent;
    // The theme re-rendered this price (size switch, cart update): its current markup is the new INR original.
    if (el.dataset.qcOut === undefined || text !== el.dataset.qcOut) {
      if (!AMOUNT.test(text)) { AMOUNT.lastIndex = 0; return; }
      AMOUNT.lastIndex = 0;
      el.dataset.qcOrig = el.innerHTML;
      el.dataset.qcText = text;
    }
    if (current === STORE) {
      el.innerHTML = el.dataset.qcOrig;
      el.dataset.qcOut = el.textContent;
      return;
    }
    const out = el.dataset.qcText.replace(AMOUNT, (_, num) => format(convert(parseFloat(num.replace(/,/g, ''))), current));
    el.textContent = out;
    el.dataset.qcOut = out;
  };

  let observer;
  const apply = () => {
    observer?.disconnect();
    document.querySelectorAll('bdi').forEach((el) => {
      if (el.closest('[data-qc-skip], select, option')) return;
      convertElement(el);
    });
    document.querySelectorAll('[data-qc-note]').forEach((note) => {
      note.hidden = current === STORE;
      const label = note.querySelector('[data-qc-note-currency]');
      if (label) label.textContent = current;
    });
    syncPickers();
    observer?.observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  const setCurrency = (code, remember) => {
    if (!supported(code)) code = STORE;
    current = code;
    if (remember) localStorage.setItem(CHOICE_KEY, code);
    apply();
  };

  const detect = async () => {
    try {
      const res = await fetch('/browsing_context_suggestions.json?country[enabled]=true&currency[enabled]=true', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      const country = data?.detected_values?.country?.handle;
      if (!country) return null;
      return COUNTRY_CURRENCY[country.toUpperCase()] || 'USD';
    } catch {
      return null;
    }
  };

  const init = async () => {
    if (!rates()) {
      // Rates unavailable: keep INR and hide the picker rather than show wrong prices.
      document.querySelectorAll('[data-qc-wrap]').forEach((wrap) => wrap.setAttribute('hidden', ''));
      return;
    }
    document.querySelectorAll('[data-qc-option]').forEach((option) => {
      option.addEventListener('click', () => {
        setCurrency(option.dataset.qcOption, true);
        option.closest('[popover]')?.hidePopover();
      });
    });

    let pending = false;
    observer = new MutationObserver(() => {
      if (pending || current === STORE) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; apply(); });
    });

    const saved = localStorage.getItem(CHOICE_KEY) || localStorage.getItem(AUTO_KEY);
    if (saved) {
      setCurrency(saved, false);
      return;
    }
    apply();
    const detected = await detect();
    if (detected) {
      localStorage.setItem(AUTO_KEY, detected);
      setCurrency(detected, false);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
